import { RankedMetric } from '../models/ranking.model'
import { RANKING_PLAYER_COLUMNS, RANKING_TEAM_COLUMNS } from '../constants/rankings.constants'
import { parseTeamRankingHeader, parseTeamRankingRows } from '../parsers/team-ranking.parser'
import { parseTeamComparisonRankingHeader } from '@features/rankings/parsers/team-comparison-ranking.parser'
import { exists } from '@core/utils/number.utils'
import {
  parseMatchPlayerRankingHeader,
  parsePlayerRankingHeader,
  parsePlayerRankingRows
} from '@features/rankings/parsers/player-ranking.parser'
import { parseMatchRankingHeader } from '@features/rankings/parsers/match-ranking.parser'
import { parsePlayerComparisonRankingHeader } from '@features/rankings/parsers/player-comparison-ranking.parser'
import { MetricFinderKey } from '@core/enums/metric.enums'
import { ComparableMetric, ListedMetric } from '@core/models/metric.models'
import { Column } from '@shared/components/table/models/table.models'
import { TeamHolder } from '@pages/team/models/team.models'
import { PlayerHolder } from '@core/models/player.models'
import { Match } from '@core/models/match.models'
import { DisplayMode } from '@core/enums/display.enums'
import { ContextMenuEntity, ContextMenuHolder } from '@core/models/context-menu.models'
import { isContextMenuValueContextEntity } from '@core/utils/context-menu.utils'
import { resolveMetricUnit, resolveMetricValue } from '@core/utils/metric.utils'
import { Aggregation } from '@features/aggregations/models/aggregations.models'
import { getAggregationAsSelectable } from '@features/aggregations/utils/aggregations.utils'

export const getContextWithCompetition = <T extends ContextMenuHolder>(metricContext: ContextMenuEntity): T => {
  const result = {
    ...(isContextMenuValueContextEntity(metricContext) ? metricContext.holder : metricContext),
    competition: metricContext.competition
  }
  return result as T
}

export const resolveRankingHeader = (
  displayMode: DisplayMode,
  metricContext: ContextMenuEntity,
  metricKey: string,
  matchDay: number
) => {
  const context: ContextMenuHolder = getContextWithCompetition(metricContext)
  switch (displayMode) {
    case DisplayMode.Team:
      return parseTeamRankingHeader(metricKey, (context as TeamHolder).team, matchDay)

    case DisplayMode.ComparedTeam:
      return parseTeamComparisonRankingHeader(metricKey, (context as TeamHolder).comparison, matchDay)

    case DisplayMode.Player:
      return parsePlayerRankingHeader(metricKey, (context as PlayerHolder).player, matchDay)
    case DisplayMode.MatchPlayer:
      return parseMatchPlayerRankingHeader(metricKey, (context as PlayerHolder).player, matchDay)

    case DisplayMode.ComparedPlayer:
    case DisplayMode.MatchComparedPlayer:
      return parsePlayerComparisonRankingHeader(metricKey, (context as PlayerHolder).comparison, matchDay)

    case DisplayMode.Match:
      return parseMatchRankingHeader(metricKey, context as Match, matchDay)
  }
}

export const resolveRankingColumns = (displayMode: DisplayMode, aggregation: Aggregation): Column[] => {
  const aggregationSelectable = getAggregationAsSelectable(aggregation)

  const aggregationColumn = {
    key: 'metricValue',
    styleClass: 'mcm-ranking-container__content-header last-column',
    label: aggregationSelectable.text,
    width: 'auto'
  }

  let columns: Column[]

  switch (displayMode) {
    case DisplayMode.Team:
    case DisplayMode.ComparedTeam:
    case DisplayMode.Match:
      columns = RANKING_TEAM_COLUMNS
      break
    case DisplayMode.Player:
    case DisplayMode.ComparedPlayer:
    case DisplayMode.MatchPlayer:
    case DisplayMode.MatchComparedPlayer:
      columns = RANKING_PLAYER_COLUMNS
      break
  }

  return [...columns, aggregationColumn]
}

export const resolveRankingRows = (
  displayMode: DisplayMode,
  rankedMetric: RankedMetric[],
  metricContext: ContextMenuEntity
) => {
  const context = getContextWithCompetition(metricContext)
  if (rankedMetric) {
    switch (displayMode) {
      case DisplayMode.Team:
      case DisplayMode.ComparedTeam:
      case DisplayMode.Match:
        return parseTeamRankingRows(rankedMetric, context)
      case DisplayMode.Player:
      case DisplayMode.MatchPlayer:
      case DisplayMode.ComparedPlayer:
      case DisplayMode.MatchComparedPlayer:
        return parsePlayerRankingRows(rankedMetric, context)
    }
  }

  return null
}

export const hasNoRankingAvailableAtAll = (payload: ListedMetric | ComparableMetric): boolean =>
  !exists(resolveMetricValue(payload, 'left')) && !exists(resolveMetricValue(payload, 'right'))

export const hasPercentageRankingOnAccumulatedMode = (
  payload: ListedMetric | ComparableMetric,
  aggregation: Aggregation
): boolean => resolveMetricUnit(payload, 'left') === 'MTR_UNIT_PORCENTAJE' && aggregation === 'Accumulated'

export const hasRankingAvailableWhenIsSingleEntity = (
  isVsEnabled: boolean,
  context: MetricFinderKey,
  aggregation: Aggregation,
  payload: ListedMetric | ComparableMetric
): boolean => {
  const homeValue = resolveMetricValue(payload, 'left')
  const awayValue = resolveMetricValue(payload, 'right')

  return (
    !isVsEnabled &&
    context !== MetricFinderKey.Match &&
    ((exists(homeValue) && aggregation === 'Average') ||
      (exists(awayValue) && aggregation === 'Accumulated') ||
      (exists(awayValue) && aggregation === 'Maximum'))
  )
}

export const hasRankingAvailableWhenIsMultiEntity = (
  isVsEnabled: boolean,
  context: MetricFinderKey,
  payload: ListedMetric | ComparableMetric
): boolean => (isVsEnabled || context === MetricFinderKey.Match) && exists(resolveMetricValue(payload, 'left'))

export const excludeNegativeRankingFromSortFn = (item: RankedMetric): boolean => +item.ranking < 0
